import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Mic, MicOff, Sparkles } from "lucide-react";
import { LANGUAGES, LOCALE_TAG } from "@/i18n";
import { EVENTS, track } from "@/lib/analytics";

/**
 * How long a wish was, without what it said.
 *
 * The text itself is the guest's own words and never leaves for a third party —
 * the restaurant already reads it, grouped and quoted verbatim, at
 * /analytics/wishes. A bucket answers the only question we have here: whether
 * anybody types more than two words into this box.
 */
const lengthBucket = (text) => {
  const n = text.length;
  if (n === 0) return "empty";
  if (n <= 20) return "short";
  if (n <= 80) return "medium";
  return "long";
};

const Q5Input = ({ onBack, onSubmit, initialText = "" }) => {
  const { t, i18n } = useTranslation();
  // Seeded from what was typed before: coming back from the plates keeps the
  // wish instead of wiping it.
  const [text, setText] = useState(initialText);
  const [recording, setRecording] = useState(false);
  const [supported, setSupported] = useState(true);
  // Dictation starts in the language the guest is reading — they are about to
  // speak the same one. It stays switchable: a Russian guest who prefers to
  // dictate their order in English should not have to change the whole app.
  const [lang, setLang] = useState(() => LOCALE_TAG[i18n.language] || LOCALE_TAG.en);
  const recognitionRef = useRef(null);
  // Whether the microphone was ever used on this screen — the feature is easy to
  // ship and hard to justify keeping if nobody presses it.
  const usedMicRef = useRef(false);

  const submit = (value, skipped) => {
    track(EVENTS.AI_WISH_SUBMITTED, {
      skipped,
      has_text: value.length > 0,
      length_bucket: lengthBucket(value),
      used_mic: usedMicRef.current,
      dictation_lang: usedMicRef.current ? lang : undefined,
    });
    onSubmit(value);
  };

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      return;
    }
    const r = new SR();
    r.lang = lang;
    r.continuous = false;
    r.interimResults = true;
    r.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setText(transcript);
    };
    r.onend = () => setRecording(false);
    r.onerror = () => setRecording(false);
    recognitionRef.current = r;
    return () => {
      try {
        r.abort();
      } catch (e) {
        // ignore
      }
    };
  }, [lang]);

  const toggleMic = () => {
    const r = recognitionRef.current;
    if (!r) return;
    if (recording) {
      r.stop();
      setRecording(false);
    } else {
      try {
        r.start();
        setRecording(true);
        usedMicRef.current = true;
      } catch (e) {
        setRecording(false);
      }
    }
  };

  return (
    <div className="px-5 pt-6 pb-10 min-h-screen flex flex-col">
      <div className="flex items-center gap-3 mb-6">
        <button
          data-testid="q5-back"
          onClick={onBack}
          className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center shrink-0"
          aria-label={t("common.back")}
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">
            {t("q5.overline")}
          </div>
          <div className="h-1.5 bg-neutral-200 rounded-full mt-1.5 overflow-hidden">
            <div className="h-full bg-[#E34A27] transition-all" style={{ width: "100%" }} />
          </div>
        </div>
      </div>

      <h1 className="text-[32px] leading-[1.1] font-black tracking-tight text-neutral-900 mb-2">
        {t("q5.title")}
      </h1>
      <p className="text-sm text-neutral-500 mb-5 max-w-[38ch]">{t("q5.subtitle")}</p>

      {/* amp-block: whatever is typed or dictated here is the guest's own words.
          A session recording masks text anyway; this box is not recorded at all. */}
      <div className="relative amp-block">
        <textarea
          data-testid="q5-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("q5.placeholder")}
          rows={5}
          className="w-full bg-white border-2 border-neutral-200 rounded-3xl p-4 pr-16 text-base text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-neutral-900 transition-colors resize-none"
        />
        {supported && (
          <button
            data-testid="q5-mic"
            onClick={toggleMic}
            aria-label={recording ? t("q5.stopRecording") : t("q5.startRecording")}
            className={`absolute bottom-3 right-3 w-11 h-11 rounded-full flex items-center justify-center transition-colors ${
              recording
                ? "bg-[#E34A27] text-white animate-pulse"
                : "bg-neutral-900 text-white"
            }`}
          >
            {recording ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
        )}
      </div>

      {supported && (
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {recording ? (
              <>
                <span className="w-2 h-2 rounded-full bg-[#E34A27] animate-pulse shrink-0" />
                <span className="text-xs font-bold text-[#E34A27]">{t("q5.listening")}</span>
              </>
            ) : (
              <span className="text-[11px] text-neutral-400">{t("q5.micHint")}</span>
            )}
          </div>
          {/* Dictation language, not interface language — hence its own control
              and its own testids, which the e2e agent already knows. */}
          <div className="flex items-center gap-1 bg-neutral-100 rounded-full p-1 shrink-0">
            {LANGUAGES.map((opt) => (
              <button
                key={opt.code}
                data-testid={`q5-lang-${opt.code}`}
                onClick={() => {
                  if (recording) {
                    try {
                      recognitionRef.current?.stop();
                    } catch (e) {
                      // ignore
                    }
                    setRecording(false);
                  }
                  setLang(LOCALE_TAG[opt.code]);
                }}
                aria-label={opt.name}
                className={`px-2.5 py-1 rounded-full text-[10px] font-black tracking-wider transition-colors ${
                  lang === LOCALE_TAG[opt.code]
                    ? "bg-neutral-900 text-white"
                    : "text-neutral-500"
                }`}
              >
                {opt.short}
              </button>
            ))}
          </div>
        </div>
      )}
      {!supported && (
        <div className="mt-2 text-[11px] text-neutral-400">{t("q5.notSupported")}</div>
      )}

      <div className="flex-1" />

      <button
        data-testid="q5-build-btn"
        onClick={() => submit(text.trim(), false)}
        className="w-full h-16 px-4 rounded-2xl bg-[#E34A27] text-white font-black text-lg tracking-wide active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
      >
        <Sparkles size={20} className="shrink-0" /> {t("q5.build")}
      </button>
      <button
        data-testid="q5-skip"
        onClick={() => submit("", true)}
        className="mt-3 w-full min-h-11 py-2 text-sm text-neutral-500 underline underline-offset-4"
      >
        {t("q5.skip")}
      </button>
    </div>
  );
};

export default Q5Input;
